/**
 * Opens every collapsed accordion that an element is nested inside.
 *
 * The element itself is left alone — only its ancestors are inspected. Rather than reaching into
 * any accordion's React state, we click the real toggle control, so the component updates exactly
 * as it would from a user click and keeps its hover states, keyboard handling and animations.
 */

const MUI_ACCORDION_ROOT = 'MuiAccordion-root'
const MUI_ACCORDION_EXPANDED = 'Mui-expanded'
const MUI_ACCORDION_SUMMARY = '.MuiAccordionSummary-root'

/**
 * A disclosure widget labelled the accessible way: a control that owns the collapsed region via
 * `aria-controls`. Covers the NSW Design System accordion and the prescription accordions.
 */
const findAriaController = (region: HTMLElement): HTMLElement | null => {
	if (!region.id) return null

	const scope = region.parentElement
	if (!scope) return null

	const controls = scope.querySelectorAll<HTMLElement>(
		'[aria-controls][aria-expanded="false"]',
	)

	return (
		Array.from(controls).find(
			(control) => control.getAttribute('aria-controls') === region.id,
		) || null
	)
}

/**
 * MUI sets `aria-expanded` on the summary but no `aria-controls`, so it needs its own rule.
 * Covers `CustomAccordion` (ACE rules, syllabus content, assessment, release notes, ...).
 */
const findMuiController = (region: HTMLElement): HTMLElement | null => {
	const isCollapsedMuiAccordion =
		region.classList.contains(MUI_ACCORDION_ROOT) &&
		!region.classList.contains(MUI_ACCORDION_EXPANDED)

	if (!isCollapsedMuiAccordion) return null

	return region.querySelector<HTMLElement>(MUI_ACCORDION_SUMMARY)
}

/**
 * @returns true when at least one accordion was opened
 */
export const expandCollapsedAncestors = (el: HTMLElement): boolean => {
	const controllers: HTMLElement[] = []

	for (
		let ancestor = el.parentElement;
		ancestor;
		ancestor = ancestor.parentElement
	) {
		const controller =
			findAriaController(ancestor) || findMuiController(ancestor)

		if (controller) {
			controllers.push(controller)
		}
	}

	// Outermost first, so an inner accordion is expanded within an already-open parent
	controllers.reverse().forEach((controller) => controller.click())

	return controllers.length > 0
}
